import React, {
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import { omitBy, isNil, sortBy, reverse } from 'lodash-es';

import InternalServerError from '@/components/errors/InternalServerError';
import LoadingScreen from '@/components/ui/loading/LoadingScreen';
import useAuthentication from '@/hooks/common/useAuthentication';
import useError, {
  buildErrorResponse,
  type ErrorResponse,
} from '@/hooks/common/useError';
import useReactQuery from '@/hooks/common/useReactQuery';
import { type ParentWithStudents } from '@/interfaces/parent';
import { AppContext } from '@/providers/ContextProvider';

const AutoLogin: React.FC<PropsWithChildren> = ({ children }) => {
  const { isLoggedIn, refreshAccessToken, login } = useAuthentication();
  const { unauthLogout } = useError();
  const { useQuery } = useReactQuery();
  const { currentUser, setCurrentUser } = useContext(AppContext);
  const [isLoadingAccessToken, setIsLoadingAccessToken] =
    useState<boolean>(true);
  const [isErrorAccessToken, setIsErrorAccessToken] = useState<boolean>(false);
  useEffect(() => {
    setIsLoadingAccessToken(true);
    void refreshAccessToken()
      .catch((e) => {
        if ((e as ErrorResponse).status === 401) {
          return;
        }
        setIsErrorAccessToken(true);
      })
      .finally(() => {
        setIsLoadingAccessToken(false);
      });
  }, [login, refreshAccessToken]);

  const { data, error, isSuccess } = useQuery<ParentWithStudents>({
    queryKey: ['me'],
    url: '/api/school/parents/me',
    enabled: !isLoadingAccessToken && isLoggedIn && !currentUser,
  });

  useEffect(() => {
    if (isSuccess && data) {
      setCurrentUser(data);
    }
  }, [isSuccess, data, setCurrentUser]);

  const isLoading =
    isLoadingAccessToken ||
    (!isLoadingAccessToken && isLoggedIn && !currentUser);

  // show error page when API responds error except 401
  useEffect(() => {
    if (error) {
      const statuses = omitBy({ error }, isNil);

      reverse(sortBy(Object.values(statuses), ['status'])).map((value) => {
        if (value?.status !== 401) {
          throw buildErrorResponse(value || {});
        }
      });
    }
  }, [unauthLogout, error]);

  if (isErrorAccessToken) {
    return <InternalServerError />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export default AutoLogin;
